/**
 * ONE DRAWN MOLECULE, DRAWN ONCE.
 *
 * ── WHY THIS EXISTS AT ALL ────────────────────────────────────────────────────────────────────
 * There are two places a structure is turned into pixels: the node view a teacher is editing,
 * and `reaction-compose`, which builds what a student sees. They have drifted apart twice
 * already — the enthalpy reached one and not the other, the Feynman flag the other and not the
 * one — and the arrows only avoided it because somebody remembered to teach both.
 *
 * Captions, electrons, per-atom colour and per-atom numbers are four more chances to drift, so
 * neither path draws a molecule any more. They call this.
 *
 * ── THE CAPTIONS ARE ORDERED BY THE DRAWING, NOT POSITIONED FROM IT ───────────────────────────
 * Each caption was first placed at its own species' centre, read off the rendered SVG. It does
 * not survive contact with real names: two species share a drawing about 90px wide and
 * "2-bromopropane" is 100px of text on its own, so the two captions sat on top of each other.
 * Widening the drawing is not available either — its width is the molecule's.
 *
 * So they are a row under the picture, in the order the species are DRAWN — which is what
 * `partSpans` is still read for, and the part that matters: with the species in a line, the
 * first caption belongs to the first molecule. Centring each one exactly would be better and
 * is not possible at the size these are drawn at.
 */

import { arrowsFromText } from './mechanism-arrows';
import { withArrows } from './mechanism-draw';
import { pruneAnnotations, readAnnotations } from './structure-annotations';
import { annotateStructure, partSpans } from './structure-overlay';
import { anchorFacts, chemistryReady, renderStructure } from './structure-render';

export type StructurePicture = {
  /** What goes INSIDE the wrapper span. Empty when nothing could be drawn. */
  html: string;
  /** What a screen reader is handed in place of the picture. Never empty if anything is. */
  label: string;
  /** The library has not landed; what is on screen must not be overwritten yet. */
  pending: boolean;
  /** The SMILES will not parse. The caller shows the source, as an unrenderable token does. */
  error: string | null;
  /** The drawing carries a caption row, which changes how the wrapper is laid out. */
  hasCaptions: boolean;
};

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function structurePicture(token: {
  smiles: string;
  name: string;
  arrows?: string;
  annotations?: string;
  /** Unique within the page: OpenChemLib puts it on every atom and bond id. */
  id: string;
  /** A highlight on the WHOLE token. Per-atom colour is finer and wins where they meet. */
  colour?: string;
}): StructurePicture {
  const name = token.name ?? '';
  if (!chemistryReady()) {
    return { html: '', label: name || token.smiles, pending: true, error: null, hasCaptions: false };
  }

  const drawn = renderStructure(token.smiles, {
    id: token.id,
    ...(token.colour ? { colour: token.colour } : {}),
  });
  if (drawn.error) {
    return { html: '', label: name || token.smiles, pending: false, error: drawn.error, hasCaptions: false };
  }

  const facts = anchorFacts(token.smiles);
  /* Pruned before anything is drawn: the SMILES is editable and these are indices into it, so
     an annotation can be left pointing at an atom that no longer exists. */
  const notes = pruneAnnotations(
    readAnnotations(token.annotations ?? ''), facts.atoms.length, facts.parts,
  );

  const arrows = arrowsFromText(token.arrows ?? '');
  const picture = annotateStructure(
    arrows.length ? withArrows(drawn.svg, arrows) : drawn.svg,
    notes,
    /* So a colour on a skeletal atom can draw the symbol it has no label for — the facts are
       already read here for the captions, so this costs nothing. */
    facts.atoms.map(a => a.label),
  );

  const captions = partSpans(drawn.svg, facts.atoms.map(a => a.part))
    /* LEFT TO RIGHT as the renderer laid them out, which is not necessarily the order the
       species are numbered in — the number is the renderer's own and the position is what a
       reader matches against. */
    .sort((a, b) => a.centre - b.centre)
    .map(s => ({ ...s, text: notes.names[s.part] ?? '' }));

  if (!captions.some(c => c.text)) {
    return { html: picture, label: name || token.smiles, pending: false, error: null, hasCaptions: false };
  }

  /*
   * A SLOT PER SPECIES, even the ones with no name.
   *
   * Two species and one caption, laid out as a row of one, centres that caption under the
   * middle of a picture where it names the right-hand half. The empty slots take their share
   * of the width and put each name over roughly its own molecule — which is as close to the
   * species as a row can get, and much closer than centring.
   */
  const row = captions
    .map(c => `<span class="reaction-caption">${escapeHtml(c.text)}</span>`)
    .join('');

  return {
    html: `<span class="reaction-structure-pic">${picture}</span>`
      + `<span class="reaction-captions">${row}</span>`,
    /*
     * THE CAPTIONS ARE THE NAME once there are any.
     *
     * The token's own `name` is one sentence for the whole picture and it goes stale the moment
     * a teacher renames one species — it is written when two molecules are joined and never
     * again. The captions are what is on screen, so they are what is said.
     */
    label: captions.map(c => c.text).filter(Boolean).join(', '),
    pending: false,
    error: null,
    hasCaptions: true,
  };
}
